import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as vendorCreationAPI from '../../api/PurchaseAPI/vendorCreationAPI';

// Async Thunks
// =============================================

export const fetchNatureofGroups = createAsyncThunk(
    'vendorCreation/fetchNatureofGroups',
    async (_, { rejectWithValue }) => {
        try { return await vendorCreationAPI.getNatureofGroups(); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch Nature of Groups'); }
    }
);

export const fetchMasterGroups = createAsyncThunk(
    'vendorCreation/fetchMasterGroups',
    async (natureGroupId, { rejectWithValue }) => {
        try { return await vendorCreationAPI.getMasterGroupsByNature(natureGroupId); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch Master Groups'); }
    }
);

export const fetchSubGroups = createAsyncThunk(
    'vendorCreation/fetchSubGroups',
    async (groupId, { rejectWithValue }) => {
        try { return await vendorCreationAPI.getSubGroupsByGroupId(groupId); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch Sub Groups'); }
    }
);

export const fetchStatesList = createAsyncThunk(
    'vendorCreation/fetchStatesList',
    async (_, { rejectWithValue }) => {
        try { return await vendorCreationAPI.getStatesList(); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch States'); }
    }
);

export const fetchAllTdsCodes = createAsyncThunk(
    'vendorCreation/fetchAllTdsCodes',
    async (_, { rejectWithValue }) => {
        try { return await vendorCreationAPI.getAllTdsCodes(); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch TDS Codes'); }
    }
);

export const fetchAllSPCategories = createAsyncThunk(
    'vendorCreation/fetchAllSPCategories',
    async (_, { rejectWithValue }) => {
        try { return await vendorCreationAPI.getAllSPCategories(); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch SP Categories'); }
    }
);

export const fetchSPCategoryDetails = createAsyncThunk(
    'vendorCreation/fetchSPCategoryDetails',
    async (val, { rejectWithValue }) => {
        try { return await vendorCreationAPI.getSPCategoryDetails(val); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch SP Category Details'); }
    }
);

export const saveNewVendor = createAsyncThunk(
    'vendorCreation/saveNewVendor',
    async (vendor, { rejectWithValue }) => {
        try { return await vendorCreationAPI.saveNewVendor(vendor); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to save Vendor'); }
    }
);

export const saveNewVendorSP = createAsyncThunk(
    'vendorCreation/saveNewVendorSP',
    async (vendor, { rejectWithValue }) => {
        try { return await vendorCreationAPI.saveNewVendorSP(vendor); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to save Service Provider Vendor'); }
    }
);

export const fetchAllVendors = createAsyncThunk(
    'vendorCreation/fetchAllVendors',
    async (_, { rejectWithValue }) => {
        try { return await vendorCreationAPI.getAllVendors(); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch Vendors'); }
    }
);

export const fetchVendorDetailsByCode = createAsyncThunk(
    'vendorCreation/fetchVendorDetailsByCode',
    async ({ vendorCode, roleId }, { rejectWithValue }) => {
        try {
            const response = await vendorCreationAPI.getVendorDetailsByCode(vendorCode, roleId);
            return { vendorCode, response };
        } catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch Vendor Details'); }
    }
);

// Initial State
// =============
const initialState = {
    natureOfGroups: [],
    masterGroups: [],
    subGroups: [],
    statesList: [],
    tdsCodes: [],
    spCategories: [],
    spCategoryDetails: null,

    vendors: [],
    vendorDetailsByCode: {}, // { [vendorCode]: Vendor }

    saveResult: null,
    saveStatus: null, // null | 'pending' | 'success' | 'failed'

    loading: {
        natureOfGroups: false,
        masterGroups: false,
        subGroups: false,
        statesList: false,
        tdsCodes: false,
        spCategories: false,
        spCategoryDetails: false,
        vendors: false,
        vendorDetails: false,
        save: false,
    },

    errors: {
        natureOfGroups: null,
        masterGroups: null,
        subGroups: null,
        statesList: null,
        tdsCodes: null,
        spCategories: null,
        spCategoryDetails: null,
        vendors: null,
        vendorDetails: null,
        save: null,
    },
};

const vendorCreationSlice = createSlice({
    name: 'vendorCreation',
    initialState,
    reducers: {
        clearMasterGroups: (state) => { state.masterGroups = []; state.errors.masterGroups = null; },
        clearSubGroups: (state) => { state.subGroups = []; state.errors.subGroups = null; },
        clearSPCategoryDetails: (state) => { state.spCategoryDetails = null; state.errors.spCategoryDetails = null; },
        clearSaveResult: (state) => { state.saveResult = null; state.saveStatus = null; state.errors.save = null; },
        resetVendorCreationForm: (state) => {
            state.masterGroups = [];
            state.subGroups = [];
            state.spCategoryDetails = null;
            state.saveResult = null;
            state.saveStatus = null;
            state.errors.masterGroups = null;
            state.errors.subGroups = null;
            state.errors.spCategoryDetails = null;
            state.errors.save = null;
        },
        resetVendorCreation: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchNatureofGroups.pending, (state) => { state.loading.natureOfGroups = true; state.errors.natureOfGroups = null; })
            .addCase(fetchNatureofGroups.fulfilled, (state, action) => { state.loading.natureOfGroups = false; state.natureOfGroups = action.payload?.Data || []; })
            .addCase(fetchNatureofGroups.rejected, (state, action) => { state.loading.natureOfGroups = false; state.errors.natureOfGroups = action.payload; state.natureOfGroups = []; });

        builder
            .addCase(fetchMasterGroups.pending, (state) => { state.loading.masterGroups = true; state.errors.masterGroups = null; })
            .addCase(fetchMasterGroups.fulfilled, (state, action) => { state.loading.masterGroups = false; state.masterGroups = action.payload?.Data || []; })
            .addCase(fetchMasterGroups.rejected, (state, action) => { state.loading.masterGroups = false; state.errors.masterGroups = action.payload; state.masterGroups = []; });

        builder
            .addCase(fetchSubGroups.pending, (state) => { state.loading.subGroups = true; state.errors.subGroups = null; })
            .addCase(fetchSubGroups.fulfilled, (state, action) => { state.loading.subGroups = false; state.subGroups = action.payload?.Data || []; })
            .addCase(fetchSubGroups.rejected, (state, action) => { state.loading.subGroups = false; state.errors.subGroups = action.payload; state.subGroups = []; });

        builder
            .addCase(fetchStatesList.pending, (state) => { state.loading.statesList = true; state.errors.statesList = null; })
            .addCase(fetchStatesList.fulfilled, (state, action) => { state.loading.statesList = false; state.statesList = action.payload?.Data || []; })
            .addCase(fetchStatesList.rejected, (state, action) => { state.loading.statesList = false; state.errors.statesList = action.payload; state.statesList = []; });

        builder
            .addCase(fetchAllTdsCodes.pending, (state) => { state.loading.tdsCodes = true; state.errors.tdsCodes = null; })
            .addCase(fetchAllTdsCodes.fulfilled, (state, action) => { state.loading.tdsCodes = false; state.tdsCodes = action.payload?.Data || []; })
            .addCase(fetchAllTdsCodes.rejected, (state, action) => { state.loading.tdsCodes = false; state.errors.tdsCodes = action.payload; state.tdsCodes = []; });

        builder
            .addCase(fetchAllSPCategories.pending, (state) => { state.loading.spCategories = true; state.errors.spCategories = null; })
            .addCase(fetchAllSPCategories.fulfilled, (state, action) => { state.loading.spCategories = false; state.spCategories = action.payload?.Data || []; })
            .addCase(fetchAllSPCategories.rejected, (state, action) => { state.loading.spCategories = false; state.errors.spCategories = action.payload; state.spCategories = []; });

        builder
            .addCase(fetchSPCategoryDetails.pending, (state) => { state.loading.spCategoryDetails = true; state.errors.spCategoryDetails = null; })
            .addCase(fetchSPCategoryDetails.fulfilled, (state, action) => {
                state.loading.spCategoryDetails = false;
                const data = action.payload?.Data || [];
                state.spCategoryDetails = data.length > 0 ? data[0] : null;
            })
            .addCase(fetchSPCategoryDetails.rejected, (state, action) => { state.loading.spCategoryDetails = false; state.errors.spCategoryDetails = action.payload; state.spCategoryDetails = null; });

        builder
            .addCase(fetchAllVendors.pending, (state) => { state.loading.vendors = true; state.errors.vendors = null; })
            .addCase(fetchAllVendors.fulfilled, (state, action) => { state.loading.vendors = false; state.vendors = action.payload?.Data || []; })
            .addCase(fetchAllVendors.rejected, (state, action) => { state.loading.vendors = false; state.errors.vendors = action.payload; state.vendors = []; });

        builder
            .addCase(fetchVendorDetailsByCode.pending, (state) => { state.loading.vendorDetails = true; state.errors.vendorDetails = null; })
            .addCase(fetchVendorDetailsByCode.fulfilled, (state, action) => {
                state.loading.vendorDetails = false;
                const { vendorCode, response } = action.payload;
                state.vendorDetailsByCode[vendorCode] = response?.Data || null;
            })
            .addCase(fetchVendorDetailsByCode.rejected, (state, action) => { state.loading.vendorDetails = false; state.errors.vendorDetails = action.payload; });

        builder
            .addCase(saveNewVendor.pending, (state) => { state.loading.save = true; state.errors.save = null; state.saveStatus = 'pending'; })
            .addCase(saveNewVendor.fulfilled, (state, action) => {
                state.loading.save = false;
                state.saveResult = action.payload;
                const responseStr = typeof action.payload?.Data === 'string' ? action.payload.Data : '';
                const [status] = responseStr.split(',');
                if (status === 'Submited') { state.saveStatus = 'success'; }
                else { state.saveStatus = 'failed'; state.errors.save = responseStr || 'Save failed'; }
            })
            .addCase(saveNewVendor.rejected, (state, action) => { state.loading.save = false; state.errors.save = action.payload; state.saveStatus = 'failed'; state.saveResult = null; });

        builder
            .addCase(saveNewVendorSP.pending, (state) => { state.loading.save = true; state.errors.save = null; state.saveStatus = 'pending'; })
            .addCase(saveNewVendorSP.fulfilled, (state, action) => {
                state.loading.save = false;
                state.saveResult = action.payload;
                const responseStr = typeof action.payload?.Data === 'string' ? action.payload.Data : '';
                const [status] = responseStr.split(',');
                if (status === 'Submited') { state.saveStatus = 'success'; }
                else { state.saveStatus = 'failed'; state.errors.save = responseStr || 'Save failed'; }
            })
            .addCase(saveNewVendorSP.rejected, (state, action) => { state.loading.save = false; state.errors.save = action.payload; state.saveStatus = 'failed'; state.saveResult = null; });
    },
});

export const {
    clearMasterGroups,
    clearSubGroups,
    clearSPCategoryDetails,
    clearSaveResult,
    resetVendorCreationForm,
    resetVendorCreation,
} = vendorCreationSlice.actions;

// Selectors
// =========
const arr = (x) => (Array.isArray(x) ? x : []);

export const selectNatureOfGroupsArray = (state) => arr(state.vendorCreation.natureOfGroups);
export const selectNatureOfGroupsLoading = (state) => state.vendorCreation.loading.natureOfGroups;

export const selectMasterGroupsArray = (state) => arr(state.vendorCreation.masterGroups);
export const selectMasterGroupsLoading = (state) => state.vendorCreation.loading.masterGroups;

export const selectSubGroupsArray = (state) => arr(state.vendorCreation.subGroups);
export const selectSubGroupsLoading = (state) => state.vendorCreation.loading.subGroups;

export const selectStatesListArray = (state) => arr(state.vendorCreation.statesList);
export const selectStatesListLoading = (state) => state.vendorCreation.loading.statesList;

export const selectTdsCodesArray = (state) => arr(state.vendorCreation.tdsCodes);
export const selectTdsCodesLoading = (state) => state.vendorCreation.loading.tdsCodes;

export const selectSPCategoriesArray = (state) => arr(state.vendorCreation.spCategories);
export const selectSPCategoriesLoading = (state) => state.vendorCreation.loading.spCategories;

export const selectSPCategoryDetails = (state) => state.vendorCreation.spCategoryDetails;

export const selectVendorsArray = (state) => arr(state.vendorCreation.vendors);
export const selectVendorsLoading = (state) => state.vendorCreation.loading.vendors;

export const selectVendorDetailsByCode = (vendorCode) => (state) => state.vendorCreation.vendorDetailsByCode[vendorCode] || null;
export const selectVendorDetailsLoading = (state) => state.vendorCreation.loading.vendorDetails;

export const selectSaveStatus = (state) => state.vendorCreation.saveStatus;
export const selectSaveLoading = (state) => state.vendorCreation.loading.save;
export const selectSaveError = (state) => state.vendorCreation.errors.save;

export default vendorCreationSlice.reducer;
